import { isOk } from "@/common/common";
import { CustomFieldDataType } from "@/customFields/customFieldsDomain";
import {
  blankUnmappedCells,
  cellEditor,
  errorCount,
  ImportFieldOption,
  resultRows,
  SpreadsheetImportGrid,
  SpreadsheetImportIssue,
  SpreadsheetImportResult,
  SpreadsheetImportRow,
} from "@/customFields/spreadsheetImportDomain";
import {
  openSpreadsheetImport,
  prepSpreadsheetImportFlow,
  setupOf,
  SpreadsheetImportFlow,
  SpreadsheetImportFlowProps,
  SpreadsheetImportState,
} from "@/customFields/spreadsheetImportFlow";
import { DCEvent, EventDispatcher } from "@/events/eventsDomain";
import { TMessage } from "@/i18n/tt";
import { ownershipImportApiClient } from "./ownershipImportApiClient";

export type OwnershipImportField =
  | "NAME"
  | "OWNER"
  | "PERCENT_HELD"
  | "DATE_FROM"
  | "DATE_TO"
  | "STATUS"
  | "OWNER_COUNTRY"
  | "OWNER_ID";

const maxOwnershipRows = 2000;

export type OwnershipImportFieldOption = {
  field: OwnershipImportField;
  dataType: CustomFieldDataType;
  required: boolean;
};

export type OwnershipImportSheet = {
  headers: string[];
  rows: SpreadsheetImportRow[];
  fields: OwnershipImportFieldOption[];
  columns: (OwnershipImportField | null)[];
  countryCodes: string[];
  fieldCountryCodes: string[];
};

export type OwnershipImportRequest = {
  rows: SpreadsheetImportRow[];
  columns: (OwnershipImportField | null)[];
};

export type OwnershipImportResponse = {
  companies: number;
  changes: number;
  issues: SpreadsheetImportIssue[];
};

type OwnershipImportSetup = Omit<OwnershipImportSheet, "rows" | "columns">;

export type OwnershipImportState = SpreadsheetImportState<
  OwnershipImportSetup,
  OwnershipImportResponse
>;

const fieldLabels: Record<OwnershipImportField, TMessage> = {
  NAME: { key: "add-company.company-name" },
  OWNER: { key: "ownership-import.field-owner" },
  PERCENT_HELD: { key: "ownership-import.field-percent-held" },
  DATE_FROM: { key: "ownership-import.field-date-from" },
  DATE_TO: { key: "ownership-import.field-date-to" },
  STATUS: { key: "transactions.ownersPerDate.companyStatus" },
  OWNER_COUNTRY: { key: "ownership-import.field-owner-country" },
  OWNER_ID: { key: "ownership-import.field-owner-id" },
};

const fieldOption = (option: OwnershipImportFieldOption): ImportFieldOption => ({
  key: option.field,
  label: fieldLabels[option.field],
  section: { key: "ownership-import.field-section" },
  editor: cellEditor(option.dataType),
});

const ownershipImportFlow: SpreadsheetImportFlow<
  OwnershipImportSetup,
  OwnershipImportRequest,
  OwnershipImportResponse
> = {
  set: (change) => ({ type: "update-store", data: { ownershipImport: change } }),
  stateOf: (root) => root.ownershipImport,
  requestOf: (state) => importRequest(state),
  validate: ownershipImportApiClient.validate,
  import: ownershipImportApiClient.import,
};

export const openOwnershipImport = (
  accountId: string,
  files: File[] | null,
  afterImport: DCEvent[],
): DCEvent[] =>
  openSpreadsheetImport(
    ownershipImportFlow,
    accountId,
    files,
    afterImport,
    ownershipImportApiClient.read,
    ({ rows, columns, ...setup }) => ({
      setup,
      rows,
      columns: columns.map((field) => field ?? undefined),
    }),
    maxOwnershipRows,
  );

const noSetup: OwnershipImportSetup = {
  headers: [],
  fields: [],
  countryCodes: [],
  fieldCountryCodes: [],
};

export const gridOf = (state: OwnershipImportState): SpreadsheetImportGrid => ({
  headers: setupOf(state, noSetup).headers,
  rows: state.rows,
  columns: state.columns,
  fieldsByKey: new Map(
    setupOf(state, noSetup).fields.map((option) => [option.field, fieldOption(option)]),
  ),
  errorsOnly: state.errorsOnly,
  selectedOnly: state.selectedOnly,
});

export const missingRequired = (state: OwnershipImportState): TMessage[] =>
  setupOf(state, noSetup)
    .fields.filter((option) => option.required && !state.columns.includes(option.field))
    .map((option) => fieldLabels[option.field]);

const fieldOf = (
  state: OwnershipImportState,
  key: string | undefined,
): OwnershipImportField | null =>
  setupOf(state, noSetup).fields.find((option) => option.field === key)?.field ?? null;

export const importRequest = (state: OwnershipImportState): OwnershipImportRequest => {
  const columns = state.columns.map((key) => fieldOf(state, key));
  return {
    rows: blankUnmappedCells(state.rows, (index) => columns[index] !== null),
    columns,
  };
};

const importResultOf = (
  state: OwnershipImportState,
  response: OwnershipImportResponse,
): SpreadsheetImportResult => ({
  counts: [
    {
      key: "companies",
      label: { key: "ownership-import.result-companies" },
      value: response.companies,
    },
    { key: "changes", label: { key: "ownership-import.result-changes" }, value: response.changes },
    {
      key: "rejected",
      label: { key: "entity-data.role-import-not-imported" },
      value: errorCount(response.issues),
    },
  ],
  nothingImported: response.companies === 0,
  rows: resultRows(gridOf(state), response.issues),
});

export type OwnershipImportViewProps = SpreadsheetImportFlowProps<OwnershipImportSetup> & {
  missingRequired: TMessage[];
  readyCompanies?: number;
  readyChanges?: number;
  canImport: boolean;
  result?: SpreadsheetImportResult;
};

export const prepOwnershipImportView = (
  state: OwnershipImportState | undefined,
  dispatch: EventDispatcher,
): OwnershipImportViewProps | undefined => {
  if (!state) {
    return undefined;
  }
  const validation = isOk(state.validation) ? state.validation.data : undefined;
  const missing = missingRequired(state);
  return {
    ...prepSpreadsheetImportFlow(
      ownershipImportFlow,
      state,
      setupOf(state, noSetup),
      gridOf(state),
      validation?.issues ?? [],
      dispatch,
    ),
    missingRequired: missing,
    readyCompanies: validation?.companies,
    readyChanges: validation?.changes,
    canImport:
      isOk(state.setupResult) &&
      missing.length === 0 &&
      (validation?.companies ?? 0) > 0 &&
      state.resultRevision === state.revision,
    result: isOk(state.importResult) ? importResultOf(state, state.importResult.data) : undefined,
  };
};
